// The you-are-here marker: where the player's own ship is, in the Solar System
// view.
//
// Not a body and not a registry row. It carries a heliocentric position and the
// angular size it is drawn at, which is everything the Atlas's own screen-space
// pick needs to weigh it beside a body's dot. The ladder, the body manifest and
// the descent all read the registry and never see it.

import * as THREE from 'three';

import type { WorldFrameHandle, WorldVec } from '../world/frame.ts';
import { inkToneMapped, eclipticToScene } from './orrery.ts';

/** What the pick answers with, and the id the focus camera is pointed at. */
export const SHIP_MARKER_ID = 'ship';

/**
 * What the reticle subtends, degrees.
 *
 * Angular and not pixels, the way `orrery.ts`'s `MIN_DRAWN_ANGULAR_DEG` floors
 * a body's dot: the marker reads the same at 11 AU and at 400 m.
 *
 * Wider than it has to be to be seen, because a body's own dot keeps the click
 * on the pixels it owns: out at the heliocentric view the ship sits on the disc
 * of whatever it is orbiting, and a reticle narrower than that dot's 12 px
 * tolerance would have no pixel of its own left to take a click on.
 */
export const SHIP_MARKER_ANGULAR_DEG = 2.5;

/**
 * The sphere the focus camera orbits, metres.
 *
 * A hull's own scale rather than a body's. It is what the band edges under the
 * marker are derived from, so the wheel bottoms out where the whole reticle is
 * still in frame.
 */
export const SHIP_MARKER_RADIUS_M = 40;

const DEG = Math.PI / 180;
const RETICLE_PX = 128;

/** A ring, four ticks and a centre dot, drawn once into a texture. */
function reticleTexture(): THREE.CanvasTexture {
  const c = document.createElement('canvas');
  c.width = RETICLE_PX;
  c.height = RETICLE_PX;
  const g = c.getContext('2d');
  if (g) {
    const m = RETICLE_PX / 2;
    g.clearRect(0, 0, RETICLE_PX, RETICLE_PX);
    // The reticle is ink: #8ee9ff as it drew at exposure 1.25 through ACES,
    // now the literal pixel (`inkToneMapped` in `orrery.ts`).
    g.strokeStyle = '#c4e3e8';
    g.fillStyle = '#c4e3e8';
    g.lineWidth = 7;
    g.beginPath();
    g.arc(m, m, 38, 0, Math.PI * 2);
    g.stroke();
    g.lineWidth = 6;
    for (const a of [0, 0.5, 1, 1.5]) {
      const dx = Math.cos(a * Math.PI);
      const dy = Math.sin(a * Math.PI);
      g.beginPath();
      g.moveTo(m + dx * 44, m + dy * 44);
      g.lineTo(m + dx * 60, m + dy * 60);
      g.stroke();
    }
    g.beginPath();
    g.arc(m, m, 9, 0, Math.PI * 2);
    g.fill();
  }
  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}

export interface ShipMarkerConfig {
  /** The camera-relative frame the orrery's own bodies are registered in. */
  frame: WorldFrameHandle;
  /** Heliocentric ecliptic J2000 metres, or null while the sim has no ship. */
  at: () => { x: number; y: number; z: number } | null;
  onNeedsRender: () => void;
}

export interface ShipMarkerHandle {
  readonly object: THREE.Object3D;
  /** Scene axes, metres, at the last update. */
  readonly world: WorldVec;
  /** False while the sim has handed back no position at all. */
  readonly known: boolean;
  /** What the reticle subtends this frame, or 0 while it is not drawn. */
  readonly drawnAngularDiameterDeg: number;
  /** The player's own switch, off the view's layer panel. */
  setLayerVisible(on: boolean): void;
  /** The bands the marker belongs in, which the ladder decides and not the player. */
  setBandVisible(on: boolean): void;
  /** Whether the camera is focused here. */
  setSelected(on: boolean): void;
  update(cameraWorld: WorldVec): void;
  dispose(): void;
}

export function createShipMarker(config: ShipMarkerConfig): ShipMarkerHandle {
  const texture = reticleTexture();
  const material = new THREE.SpriteMaterial({
    map: texture,
    transparent: true,
    toneMapped: inkToneMapped(),
    // The marker says where the ship IS, so a planet in front of it must not
    // take it off screen: an occluded you-are-here is a marker that lies.
    depthTest: false,
    depthWrite: false,
    opacity: 0.85,
    color: 0xffffff,
  });
  const sprite = new THREE.Sprite(material);
  sprite.name = 'atlas-ship-marker';
  sprite.renderOrder = 30;
  sprite.visible = false;
  sprite.userData['bodyId'] = SHIP_MARKER_ID;

  const world: WorldVec = { x: 0, y: 0, z: 0 };
  let known = false;
  let layerOn = true;
  let bandOn = false;
  let drawnDeg = 0;

  const unregister = config.frame.register(sprite, () => world);

  function update(cameraWorld: WorldVec): void {
    const at = config.at();
    if (at === null) {
      known = false;
      drawnDeg = 0;
      if (sprite.visible) { sprite.visible = false; config.onNeedsRender(); }
      return;
    }
    known = true;
    const w = eclipticToScene(at, 1);
    world.x = w.x;
    world.y = w.y;
    world.z = w.z;
    const show = layerOn && bandOn;
    if (sprite.visible !== show) { sprite.visible = show; config.onNeedsRender(); }
    if (!show) { drawnDeg = 0; return; }
    const d = Math.hypot(w.x - cameraWorld.x, w.y - cameraWorld.y, w.z - cameraWorld.z);
    // Behind the eye the sprite is still placed, and its own size stops meaning
    // anything: the pick reads this, so it reads 0 rather than a width the
    // projection would put somewhere it is not drawn.
    const span = 2 * Math.tan(SHIP_MARKER_ANGULAR_DEG * 0.5 * DEG) * Math.max(1, d);
    sprite.scale.set(span, span, 1);
    drawnDeg = SHIP_MARKER_ANGULAR_DEG;
  }

  return {
    object: sprite,
    world,
    get known(): boolean { return known; },
    get drawnAngularDiameterDeg(): number { return drawnDeg; },
    setLayerVisible(on: boolean): void {
      if (layerOn === on) return;
      layerOn = on;
      config.onNeedsRender();
    },
    setBandVisible(on: boolean): void {
      if (bandOn === on) return;
      bandOn = on;
      config.onNeedsRender();
    },
    setSelected(on: boolean): void {
      const want = on ? 1 : 0.85;
      if (material.opacity === want) return;
      material.opacity = want;
      // The tint multiplies a texture that already carries the retuned ink, so
      // it is the per-channel ratio of the drawn pixel and not a colour.
      material.color.setHex(on ? 0xffffff : 0xe6f7ff);
      config.onNeedsRender();
    },
    update,
    dispose(): void {
      unregister();
      material.dispose();
      texture.dispose();
    },
  };
}
